import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Calendar, Clock, Users, BarChart3, ArrowRight, CheckCircle,
  Zap, Shield, Sparkles, ArrowUpRight, Timer, FileSpreadsheet,
  MapPin, Award, CalendarRange, Globe, LayoutDashboard, ArrowLeftRight,
  FileText, Bell, Smartphone, Library, Brain, ClipboardCheck,
} from 'lucide-react';
import Footer from '../../components/Footer';
import PublicNavbar from '../../components/PublicNavbar';
import AppDownloadLinks from '../../components/AppDownloadLinks';
import cogzImage from '../../assets/cogz.png';

export default function LandingPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t(
      'landing.seoTitle',
      'BuildMyShifts | AI Shift Scheduling, Staff Training and Policy Compliance'
    );

    const description = t(
      'landing.seoDescription',
      'BuildMyShifts combines AI rota planning, time clocking, payroll export, staff training records, policy sign-off and an AI Knowledge Hub for workforce compliance.'
    );
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
  }, [t]);

  const features = [
    { icon: Calendar, title: t('landing.f1Title', 'Smart Rota Builder'), desc: t('landing.f1Desc', 'Create and publish weekly rotas with drag-and-drop simplicity. The more rotas you build, the smarter it gets — it learns from your patterns to auto-generate future schedules.') },
    { icon: Sparkles, title: t('landing.f2Title', 'AI Auto-Fill'), desc: t('landing.f2Desc', 'AI assigns the right staff based on reliability, fairness, preferences, contract hours, and scheduling rules.') },
    { icon: Timer, title: t('landing.f3Title', 'Time Clock & QR'), desc: t('landing.f3Desc', 'Staff clock in and out via QR code scanning. Live timers, daily records, and location-based check-ins.') },
    { icon: Smartphone, title: t('landing.f17Title', 'Clocking Terminal Mode'), desc: t('landing.f17Desc', 'Turn the mobile app into a locked clocking terminal for one location. Staff can clock in with QR cards or their own staff PIN, and admins can issue branded clock cards with names and photos.') },
    { icon: FileSpreadsheet, title: t('landing.f4Title', 'Payroll Export'), desc: t('landing.f4Desc', 'Export time records to Excel or CSV — with built-in formats for Xero, QuickBooks, Sage, ADP, Gusto and more.') },
    { icon: BarChart3, title: t('landing.f5Title', 'Analytics Dashboard'), desc: t('landing.f5Desc', '4-tab analytics: overview, time tracking, staff insights, and scheduling — with charts, leaderboards, and overtime alerts.') },
    { icon: Shield, title: t('landing.f6Title', 'Shift Swaps'), desc: t('landing.f6Desc', 'Staff request shift swaps. Managers approve with one click and full compliance verification.') },
    { icon: MapPin, title: t('landing.f7Title', 'Groups & Locations'), desc: t('landing.f7Desc', 'Organise staff into departments or locations. Colour-coded groups with QR codes for quick clock-ins.') },
    { icon: Award, title: t('landing.f8Title', 'Staff Accreditations'), desc: t('landing.f8Desc', 'Track certifications, licences, and training records with file uploads and expiry date monitoring.') },
    { icon: Clock, title: t('landing.f9Title', 'WTD Compliance'), desc: t('landing.f9Desc', 'Automatic checks for rest periods, max weekly hours, daily limits, and consecutive day rules.') },
    { icon: Users, title: t('landing.f10Title', 'Staff Management'), desc: t('landing.f10Desc', 'Manage skills, availability, contract hours, and role-based permissions for every team member.') },
    { icon: CalendarRange, title: t('landing.f11Title', 'Multi-Week Views'), desc: t('landing.f11Desc', 'View and manage rotas across 1, 2, or 3 weeks — or 1, 2, or 3 months — for long-term planning.') },
    { icon: Globe, title: t('landing.f12Title', '10 Languages'), desc: t('landing.f12Desc', 'Full localisation in English, Spanish, French, German, Portuguese, Chinese, Arabic, Hindi, Polish, and Romanian.') },
    { icon: LayoutDashboard, title: t('landing.f13Title', 'Staff Self-Service Dashboard'), desc: t('landing.f13Desc', 'Every employee gets a personal dashboard showing their upcoming shifts, weekly hours, swap requests, and availability at a glance.') },
    { icon: ArrowLeftRight, title: t('landing.f14Title', 'Shift Swap Board'), desc: t('landing.f14Desc', 'A self-service marketplace where staff browse open shifts, post shifts for swap, and pick up available slots — with manager approval built in.') },
    { icon: FileText, title: t('landing.f15Title', 'Reports & Export'), desc: t('landing.f15Desc', 'Generate detailed attendance, overtime, cost, and performance reports with date range filters. Export to CSV or print with one click.') },
    { icon: Bell, title: t('landing.f16Title', 'Notifications Centre'), desc: t('landing.f16Desc', 'Real-time notification bell with a full inbox — filter by type, bulk actions, mark as read, and never miss a shift change or swap request.') },
    { icon: Library, title: t('landing.f18Title', 'Policies & Procedures'), desc: t('landing.f18Desc', 'Upload controlled documents, track policy versions, assign staff sign-off, record training evidence, and keep procedures shared with BuildMyChecks.') },
    { icon: Brain, title: t('landing.f19Title', 'AI Knowledge Hub'), desc: t('landing.f19Desc', 'Ask natural language questions across approved policy summaries and help staff find the right procedure without rescanning large documents.') },
  ];

  const pricingFeatures = [
    t('pricing.feat1', 'Unlimited rotas & shifts'),
    t('pricing.feat2', 'AI auto-fill scheduling'),
    t('pricing.feat3', 'Time clock with QR scanning'),
    t('pricing.feat4', 'Payroll export (12 formats)'),
    t('pricing.feat5', 'Analytics & reporting dashboard'),
    t('pricing.feat6', 'Shift swap management'),
    t('pricing.feat7', 'Staff availability tracking'),
    t('pricing.feat8', 'Groups & locations'),
    t('pricing.feat9', 'Staff accreditations & expiry tracking'),
    t('pricing.feat10', 'WTD compliance checks'),
    t('pricing.feat11', 'Multi-week & multi-month views'),
    t('pricing.feat12', 'Role-based access control'),
    t('pricing.feat13', 'Multi-language support (10 languages)'),
    t('pricing.feat14', '14-day free trial'),
    t('pricing.feat17', 'Staff self-service dashboard'),
    t('pricing.feat18', 'Shift swap board & marketplace'),
    t('pricing.feat19', 'Reports & CSV/print export'),
    t('pricing.feat20', 'Full audit log & activity trail'),
    t('pricing.feat21', 'Notifications centre & real-time alerts'),
    t('pricing.feat22', 'Policies, procedures & staff sign-off'),
    t('pricing.feat23', 'AI Knowledge Hub for policy search'),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-20 md:py-32">
          <div className="max-w-3xl mx-auto flex flex-col items-center">
            <img src="/logo.png" alt="BuildMyShifts" className="w-24 h-24 object-contain mb-6 drop-shadow-xl" />
            <div className="inline-flex items-center gap-2 bg-blue-50 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-6">
              <Zap size={14} /> {t('landing.badge', 'AI-Powered Shift Planning')}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-6 text-center">
              {t('landing.heroTitle', 'Smart shift scheduling for')} <span className="text-primary">{t('landing.heroHighlight', 'modern teams')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed text-center">
              {t('landing.heroDesc', 'Build rotas in minutes with AI-powered staff assignments. Track hours with QR clock-ins or terminal mode, export payroll in one click, and keep your team compliant — all in one platform.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-primary text-white px-8 py-3.5 rounded-xl text-lg font-semibold hover:bg-primary-dark transition-colors inline-flex items-center gap-2 justify-center"
              >
                {t('publicNav.startTrial', 'Start Free Trial')} <ArrowRight size={20} />
              </Link>
              <a
                href="#features"
                className="border-2 border-gray-200 text-gray-700 px-8 py-3.5 rounded-xl text-lg font-semibold hover:border-gray-300 transition-colors inline-flex items-center gap-2 justify-center"
              >
                {t('landing.seeFeatures', 'See Features')}
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-500 text-center">{t('landing.trialNote', '14-day free trial. No credit card required.')}</p>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-blue-50 to-transparent pointer-events-none"></div>
      </section>

      {/* Features */}
      <section id="features" className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.featuresTitle', 'Everything you need to manage your workforce')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('landing.featuresSubtitle', 'From rota creation to payroll export, BuildMyShifts handles the full shift lifecycle so you can focus on your team.')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-5">
                  <f.icon size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Knowledge Hub */}
      <section className="bg-white py-20 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-start gap-5 mb-6">
                <div className="cogz-assistant-orb">
                  <img src={cogzImage} alt="Cogz AI compliance assistant for BuildMyShifts policy search and staff training records" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 bg-blue-50 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-5">
                    <Brain size={15} />
                    {t('landing.knowledgeBadge', 'Cogz AI compliance assistant')}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                    {t('landing.cogzKnowledgeTitle', 'Meet Cogz: AI policy search and staff training records for shift-based teams')}
                  </h2>
                </div>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {t('landing.cogzKnowledgeBody', 'Cogz brings AI-powered policy assistance into BuildMyShifts, helping managers centralise controlled documents, track staff acknowledgements, record assessment evidence, and answer workforce compliance questions using approved policy summaries.')}
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                {t('landing.cogzKnowledgeSeoText', 'Use Cogz as an AI Knowledge Hub assistant for rota teams, HR compliance, staff training records, policy sign-off, procedure search, assessment evidence and audit-ready workforce reporting.')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  t('landing.knowledgePoint1', 'Shared policies with BuildMyChecks to avoid duplicate uploads'),
                  t('landing.knowledgePoint2', 'Version control, expiry dates and review intervals'),
                  t('landing.knowledgePoint3', 'Digital sign-off with training records and audit evidence'),
                  t('landing.knowledgePoint4', 'AI policy search for quick answers on web and mobile'),
                ].map((item) => (
                  <div key={item} className="flex gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/features"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors"
              >
                {t('landing.knowledgeCta', 'Explore compliance features')} <ArrowRight size={18} />
              </Link>
            </div>

            <div className="rounded-3xl bg-slate-950 p-6 shadow-2xl text-white overflow-hidden">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                  <ClipboardCheck size={26} className="text-blue-300" />
                </div>
                <div>
                  <p className="text-sm text-blue-200">{t('landing.knowledgePanelEyebrow', 'Compliance snapshot')}</p>
                  <h3 className="text-xl font-bold">{t('landing.knowledgePanelTitle', 'Policies ready for audit')}</h3>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  [t('landing.knowledgeMetric1', 'Policy sign-offs completed'), '92%'],
                  [t('landing.knowledgeMetric2', 'Overdue acknowledgements'), '3'],
                  [t('landing.knowledgeMetric3', 'Assessment pass rate'), '88%'],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between rounded-2xl bg-white/10 border border-white/10 p-4">
                    <span className="text-sm text-slate-200">{label}</span>
                    <span className="text-2xl font-bold text-white">{value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-2xl bg-blue-500/10 border border-blue-400/20 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-blue-200 mb-2">{t('landing.knowledgeAskLabel', 'Ask AI')}</p>
                <p className="text-sm text-slate-100">
                  {t('landing.knowledgeAskExample', '"What PPE is required for night shift cleaning?"')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bundle Banner */}
      <section className="bg-gradient-to-r from-primary to-secondary py-16">
        <div className="max-w-4xl mx-auto px-4 text-center text-white flex flex-col items-center">
          <img src="/bmc-logo.png" alt="BuildMyChecks" className="w-28 h-28 object-contain rounded-2xl mx-auto mb-4 shadow-2xl border-4 border-white/20" />
          <h2 className="text-3xl font-bold mb-4">{t('landing.bundleTitle', 'Already using BuildMyChecks?')}</h2>
          <p className="text-lg opacity-90 mb-6">
            {t('landing.bundleDesc', 'Get BuildMyShifts at 50% off! Just £2.50/month when bundled with your BuildMyChecks subscription.')}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-white text-primary px-8 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
          >
            {t('landing.bundleCta', 'Activate Bundle Pricing')} <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('pricing.title', 'Simple, transparent pricing')}</h2>
            <p className="text-lg text-gray-600">{t('pricing.subtitle', 'One plan with everything included. No hidden fees.')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border-2 border-primary p-8">
              <div className="inline-block bg-blue-50 text-primary text-sm font-medium px-3 py-1 rounded-full mb-4">{t('pricing.popular', 'Most Popular')}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('pricing.standalone', 'Standalone')}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-gray-900">£5</span>
                <span className="text-gray-500">{t('pricing.perMonth', '/month per user')}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {pricingFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="block w-full bg-primary text-white text-center py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors"
              >
                {t('publicNav.startTrial', 'Start Free Trial')}
              </Link>
            </div>
            <div className="bg-blue-50 rounded-2xl border-2 border-secondary p-8">
              <div className="inline-block bg-blue-100 text-secondary text-sm font-medium px-3 py-1 rounded-full mb-4">{t('pricing.bestValue', 'Best Value')}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('pricing.bundleName', 'Bundle with BuildMyChecks')}</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-gray-900">£2.50</span>
                <span className="text-gray-500">{t('pricing.perMonth', '/month per user')}</span>
              </div>
              <p className="text-sm text-green-600 font-medium mb-6">{t('pricing.bundleSave', 'Save 50% with your BuildMyChecks subscription')}</p>
              <ul className="space-y-3 mb-8">
                {pricingFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="block w-full bg-secondary text-white text-center py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                {t('publicNav.startTrial', 'Start Free Trial')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-900 py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('landing.ctaTitle', 'Ready to simplify your shift planning?')}</h2>
          <p className="text-gray-400 text-lg mb-8">
            {t('landing.ctaDesc', 'Join businesses already using BuildMyShifts to save time and improve scheduling.')}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3.5 rounded-xl text-lg font-semibold hover:bg-primary-dark transition-colors"
          >
            {t('landing.ctaButton', 'Get Started Free')} <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <section className="bg-white py-20 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 border border-gray-200 rounded-3xl p-8 md:p-10 shadow-sm">
            <div className="max-w-2xl mb-8">
              <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm font-medium text-gray-700 mb-4">
                <Sparkles size={14} className="text-primary" />
                {t('appDownloads.badge', 'Mobile access for teams on the move')}
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                {t('appDownloads.landingTitle', 'Take BuildMyShifts with you')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('appDownloads.landingBody', 'Managers and staff can view schedules, track time, and manage swaps from the mobile app.')}
              </p>
            </div>
            <AppDownloadLinks />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
